/**
 * In-memory TTL cache for Image Generator catalog API responses.
 * Dedupes in-flight requests with the same key so React remounts / twin
 * useEffects don't double-hit the backend.
 */

const DEFAULT_TTL_MS = 10 * 60 * 1000;

type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

const values = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();

const normalizePart = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const imageGeneratorCatalogCacheKey = (
  kind: string,
  parts: Record<string, unknown>
): string => {
  const body = Object.keys(parts)
    .sort()
    .map(key => `${key}=${normalizePart(parts[key])}`)
    .join("&");
  return `${kind}?${body}`;
};

export const getCachedImageGeneratorCatalog = async <T>(
  key: string,
  loader: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS
): Promise<T> => {
  const hit = values.get(key);
  if (hit && hit.expiresAt > Date.now()) {
    return hit.value as T;
  }

  const pending = inflight.get(key);
  if (pending) {
    return pending as Promise<T>;
  }

  const request = loader()
    .then(value => {
      values.set(key, {
        value,
        expiresAt: Date.now() + ttlMs,
      });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, request);
  return request;
};
